import { useMergeRefs } from '@floating-ui/react'
import { ComponentProps, KeyboardEventHandler, MouseEventHandler, ReactNode, useEffect, useRef } from 'react'

import { useClickOutside } from '../../hooks/useClickOutside'
import { useVirtualizeRows } from '../../hooks/useVirtualizeRows'
import { astraTwMerge } from '../Utilities/astraTwMerge'
import { BodyRow, CellHeight, cellHeightStyle, SectionRows, TableBody } from './components'
import { useDataGridContext } from './hooks/useDataGridContext'
import { EMPTY_ROW_SELECTION, useGridRowSelection } from './hooks/useGridRowSelection'
import { useRecalculatePositionOnNavigation } from './hooks/useRecalculatePositionOnNavigation'
import { Id, RowType } from './types'
import { getGridCursor, isDefined, toCellId } from './utils'

export type DataGridProps<TRow extends { id: Id }, TCol extends { id: Id }> = Omit<ComponentProps<'div'>, 'children'> & {
    cols: TCol[]
    rows: TRow[]

    body: {
        rowHeader?: (row: TRow) => ReactNode
        rowClassName?: (row: TRow) => string

        cellRender: (row: TRow, col: TCol, isSelected: boolean) => ReactNode
        cellClassName?: (row: TRow, col: TCol) => string
    }

    headers?: (RowType<TCol> | null)[]
    footers?: (RowType<TCol> | null)[]

    /** A pinned display column; its cells are excluded from selection and navigation. */
    summary?: {
        header?: ReactNode
        cellRender: (row: TRow) => ReactNode
        footer?: ReactNode
        width?: number
    } | null

    fullWidthCells?: boolean
    firstColumnWidth?: number
    allowClickOutside?: boolean

    /**
     * If set to valid value, datagrid will add virtualization to the body.
     * This will improve performance for large datasets but requires
     * predefined cell height.
     * */
    optimizeRendering?: { cellHeight?: CellHeight }
}

function hasContent(node: ReactNode) {
    return node !== undefined && node !== null && node !== false && node !== ''
}

function getGridCell(target: EventTarget, grid: HTMLElement) {
    if (!(target instanceof Element)) return null
    const cell = target.closest<HTMLElement>('[data-cell]')
    return cell?.closest('[data-grid]') === grid ? cell : null
}

export function DataGrid<TRow extends { id: Id }, TCol extends { id: Id }>({
    cols,
    rows,
    body,
    headers,
    footers,
    summary,
    onClick,
    onKeyDown,
    className,
    style,
    firstColumnWidth = 64,
    fullWidthCells,
    allowClickOutside = false,
    optimizeRendering,
    ...props
}: DataGridProps<TRow, TCol>) {
    const isCellFullWidth = fullWidthCells ? 'minmax(28px, 1fr)' : 'minmax(28px, 190px)'
    const dataGridTemplateColumns = body.rowHeader
        ? `${firstColumnWidth}px repeat(${cols.length}, ${isCellFullWidth})`
        : `repeat(${cols.length}, minmax(28px, 1fr))`

    const summaryWidth = summary ? (summary.width ?? 80) : 0
    const gridTemplateColumns = summary ? `${dataGridTemplateColumns} ${summaryWidth}px` : dataGridTemplateColumns
    const sectionColumns = summary ? `${(body.rowHeader ? firstColumnWidth : 0) + cols.length * 28}px` : undefined
    const sectionStyle = summary ? { gridTemplateColumns: `minmax(${sectionColumns}, 1fr) ${summaryWidth}px` } : undefined
    const headerRows = headers?.filter(isDefined) ?? []
    const footerRows = footers?.filter(isDefined) ?? []
    const hasHeader = headerRows.length > 0 || hasContent(summary?.header)
    const hasFooter = footerRows.length > 0 || hasContent(summary?.footer)

    const { cursor, selectedCells, virtualSelectedCells, setDefault, onMouseUp, onKeyDown: navigate } = useDataGridContext()
    const rowSelections = useGridRowSelection(selectedCells, virtualSelectedCells)

    const dataGridRef = useRef<HTMLDivElement>(null)
    const headerRef = useRef<HTMLDivElement>(null)
    const bodyRef = useRef<HTMLDivElement>(null)
    const footerRef = useRef<HTMLDivElement>(null)
    const previousFocusedCursorIdRef = useRef<string | undefined>(undefined)

    const rowHeight = cellHeightStyle[optimizeRendering?.cellHeight ?? 'tall']
    // Range is already clamped to the row count by the hook; nominal row height
    // is the exact pitch here since DataGrid borders sit on fixed-height cells.
    const {
        start: visibleStart,
        end: visibleEnd,
        ref: virtualizerRef,
    } = useVirtualizeRows({
        rowCount: rows.length,
        nominalRowHeight: rowHeight,
        overscan: 2,
        enabled: optimizeRendering !== undefined,
    })
    const gridRef = useMergeRefs([dataGridRef, virtualizerRef])

    const handleClickOutside = () => {
        if (allowClickOutside) return
        setDefault()
    }

    useRecalculatePositionOnNavigation({ firstColumnWidth: body.rowHeader ? firstColumnWidth : 0, dataGridRef, cursor, headerRef, footerRef, summaryWidth })

    useClickOutside(bodyRef, handleClickOutside)

    useEffect(() => {
        const cursorId = cursor ? toCellId(cursor) : undefined

        if (!cursorId) {
            previousFocusedCursorIdRef.current = undefined
            return
        }

        if (previousFocusedCursorIdRef.current === cursorId) return

        const cursorCellEl = dataGridRef.current ? getGridCursor(dataGridRef.current) : undefined
        if (!cursorCellEl) return

        previousFocusedCursorIdRef.current = cursorId
        cursorCellEl.focus({ preventScroll: true })
    }, [cursor])

    const handleClick: MouseEventHandler<HTMLDivElement> = event => {
        onClick?.(event)
        if (event.defaultPrevented) return
        const cell = getGridCell(event.target, event.currentTarget)
        if (!cell) return
        onMouseUp(event, cell.dataset.rowId!, cell.dataset.colId!)
        event.preventDefault()
    }

    const handleKeyDown: KeyboardEventHandler<HTMLDivElement> = event => {
        onKeyDown?.(event)
        if (event.defaultPrevented || !getGridCell(event.target, event.currentTarget)) return
        if (event.key === 'Escape') {
            setDefault()
            return
        }
        navigate(event)
    }

    const renderRow = (row: TRow, height?: number) => (
        <BodyRow
            key={row.id}
            row={row}
            cols={cols}
            selection={rowSelections.get(String(row.id)) ?? EMPTY_ROW_SELECTION}
            cursorColId={cursor && String(cursor.rowId) === String(row.id) ? String(cursor.colId) : undefined}
            rowHeader={body.rowHeader}
            rowClassName={body.rowClassName}
            cellClassName={body.cellClassName}
            height={height}
            gridTemplateColumns={gridTemplateColumns}
            summaryCellRender={summary?.cellRender}
        >
            {body.cellRender}
        </BodyRow>
    )

    return (
        <div
            {...props}
            ref={gridRef}
            data-grid=""
            onClick={handleClick}
            onKeyDown={handleKeyDown}
            className={astraTwMerge('thin-scroll isolate grid h-full max-h-full max-w-full select-none overflow-auto bg-surface-base', className)}
            style={{ ...style, gridTemplateRows: 'auto 1fr auto' }}
        >
            {hasHeader && (
                <div ref={headerRef} className="sticky top-0 z-priority row-start-1 grid whitespace-nowrap border-b-2" style={sectionStyle}>
                    <SectionRows
                        rows={headerRows}
                        cols={cols}
                        gridTemplateColumns={dataGridTemplateColumns}
                        withSummary={!!summary}
                        summaryContent={summary?.header}
                    />
                </div>
            )}

            <div ref={bodyRef} className="isolate row-start-2 grid h-full auto-rows-min">
                {optimizeRendering ? (
                    <TableBody rowCount={rows.length} rowHeight={rowHeight} start={visibleStart} end={visibleEnd}>
                        {rows.slice(visibleStart, visibleEnd).map(row => renderRow(row, rowHeight))}
                    </TableBody>
                ) : (
                    rows.map(row => renderRow(row))
                )}
            </div>

            {hasFooter && (
                <div ref={footerRef} className="sticky bottom-0 z-priority row-start-3 grid whitespace-nowrap border-t-2" style={sectionStyle}>
                    <SectionRows
                        rows={footerRows}
                        cols={cols}
                        gridTemplateColumns={dataGridTemplateColumns}
                        withSummary={!!summary}
                        summaryContent={summary?.footer}
                    />
                </div>
            )}
        </div>
    )
}
